// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file BankingBills.tsx
// @author Arlinda Peixoto
// @company Pagali
// @description Página de pagamento de serviços/faturas
// @date 2026-01-28
// ------------------------------------------------------------

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bankingService } from '../services';
import { useToast } from '../contexts/ToastContext';
import { Card, LoaderComponent, Button, Modal } from '../components/ui';
import { fmtParede } from '../utils/tempoCv';
import type {
  ServiceProvider,
  BillDetails,
  BillFavorite,
  BillPaymentResult,
  ServiceProviderCategory,
} from '../types';
import {
  Receipt,
  Zap,
  Droplets,
  Wifi,
  Phone,
  Tv,
  Shield,
  Building,
  GraduationCap,
  MoreHorizontal,
  Star,
  StarOff,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Search,
} from 'lucide-react';

const CATEGORY_CONFIG: Record<ServiceProviderCategory, { icon: React.ReactNode; label: string; color: string }> = {
  electricity: { icon: <Zap size={24} />, label: 'Eletricidade', color: '#FFC107' },
  water: { icon: <Droplets size={24} />, label: 'Água', color: '#2196F3' },
  telecom: { icon: <Phone size={24} />, label: 'Telecomunicações', color: '#4CAF50' },
  internet: { icon: <Wifi size={24} />, label: 'Internet', color: '#9C27B0' },
  tv: { icon: <Tv size={24} />, label: 'TV', color: '#E91E63' },
  insurance: { icon: <Shield size={24} />, label: 'Seguros', color: '#00BCD4' },
  government: { icon: <Building size={24} />, label: 'Governo', color: '#607D8B' },
  education: { icon: <GraduationCap size={24} />, label: 'Educação', color: '#FF5722' },
  other: { icon: <MoreHorizontal size={24} />, label: 'Outros', color: '#9E9E9E' },
};

const BankingBills: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [paying, setPaying] = useState(false);

  // Data
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [favorites, setFavorites] = useState<BillFavorite[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<ServiceProviderCategory | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<ServiceProvider | null>(null);
  const [billDetails, setBillDetails] = useState<BillDetails | null>(null);

  // Form
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState('');
  const [pin, setPin] = useState('');
  const [saveAsFavorite, setSaveAsFavorite] = useState(false);
  const [favoriteAlias, setFavoriteAlias] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Result
  const [result, setResult] = useState<BillPaymentResult | null>(null);
  const [showResult, setShowResult] = useState(false);

  // Steps
  const [step, setStep] = useState<'category' | 'provider' | 'reference' | 'confirm'>('category');

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Pagar Serviços');
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [providersData, favoritesData] = await Promise.allSettled([
        bankingService.getProviders(),
        bankingService.getBillFavorites(),
      ]);

      if (providersData.status === 'fulfilled') setProviders(providersData.value);
      if (favoritesData.status === 'fulfilled') setFavorites(favoritesData.value);
    } catch (err) {
      showError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (value: number | undefined | null): string => {
    const safe = typeof value === 'number' && !isNaN(value) ? value : 0;
    return new Intl.NumberFormat('pt-CV', {
      style: 'currency',
      currency: 'CVE',
      minimumFractionDigits: 0,
    }).format(safe);
  };

  const getCategories = (): ServiceProviderCategory[] => {
    const categories = new Set(providers.map((p) => p.category));
    return Array.from(categories) as ServiceProviderCategory[];
  };

  const getProvidersByCategory = (category: ServiceProviderCategory): ServiceProvider[] => {
    return providers.filter((p) => p.category === category);
  };

  const handleCategorySelect = (category: ServiceProviderCategory) => {
    setSelectedCategory(category);
    setStep('provider');
  };

  const handleProviderSelect = (provider: ServiceProvider) => {
    setSelectedProvider(provider);
    setReference('');
    setBillDetails(null);
    setStep('reference');
  };

  const handleFavoriteSelect = async (favorite: BillFavorite) => {
    const provider = providers.find((p) => p.id === favorite.providerId);
    if (provider) {
      setSelectedProvider(provider);
      setReference(favorite.reference);
      setStep('reference');
      // Auto-validate
      await validateBill(provider.id, favorite.reference);
    }
  };

  const validateBill = async (providerId?: number, ref?: string) => {
    const provId = providerId || selectedProvider?.id;
    const refValue = ref || reference;

    if (!provId || !refValue) return;

    setValidating(true);
    try {
      const details = await bankingService.validateBill(provId, refValue);
      setBillDetails(details);
      if (details.amount) {
        setAmount(details.amount.toString());
      }
      setStep('confirm');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Referência inválida');
    } finally {
      setValidating(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedProvider || !reference || !pin) {
      showError('Preencha todos os campos');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      showError('Insira um valor válido');
      return;
    }

    if (pin.length < 4) {
      showError('PIN deve ter 4 dígitos');
      return;
    }

    setPaying(true);
    try {
      const paymentResult = await bankingService.payBill({
        providerId: selectedProvider.id,
        reference,
        amount: parseFloat(amount),
        pin,
        saveAsFavorite,
      });

      setResult(paymentResult);
      setShowResult(true);
      showSuccess('Pagamento realizado com sucesso!');

      // If saved as favorite, add alias
      if (saveAsFavorite && favoriteAlias) {
        await bankingService.addBillFavorite(selectedProvider.id, reference, favoriteAlias);
        loadInitialData(); // Reload favorites
      }

      // Reset form
      resetForm();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Erro no pagamento');
    } finally {
      setPaying(false);
    }
  };

  const resetForm = () => {
    setSelectedCategory(null);
    setSelectedProvider(null);
    setBillDetails(null);
    setReference('');
    setAmount('');
    setPin('');
    setSaveAsFavorite(false);
    setFavoriteAlias('');
    setStep('category');
  };

  const goBack = () => {
    switch (step) {
      case 'provider':
        setStep('category');
        setSelectedCategory(null);
        break;
      case 'reference':
        setStep('provider');
        setSelectedProvider(null);
        setBillDetails(null);
        break;
      case 'confirm':
        setStep('reference');
        setBillDetails(null);
        break;
    }
  };

  const filteredFavorites = favorites.filter(
    (f) =>
      f.alias.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.providerName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoaderComponent />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => (step === 'category' ? navigate('/banking') : goBack())}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ChevronLeft size={24} />
        </button>
        <h1 className="text-2xl font-bold">Pagar Serviços</h1>
      </div>

      {/* Favorites Section */}
      {step === 'category' && favorites.length > 0 && (
        <Card>
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Star size={20} className="text-yellow-500" />
              <h2 className="font-semibold">Favoritos</h2>
            </div>
          </div>
          <div className="p-4">
            <div className="relative mb-4">
              <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Pesquisar favoritos..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {filteredFavorites.slice(0, 4).map((favorite) => (
                <button
                  key={favorite.id}
                  onClick={() => handleFavoriteSelect(favorite)}
                  className="p-3 border rounded-lg hover:bg-gray-50 text-left transition-colors"
                >
                  <p className="font-medium truncate">{favorite.alias}</p>
                  <p className="text-sm text-gray-500 truncate">{favorite.providerName}</p>
                  {favorite.lastPaymentAmount && (
                    <p className="text-xs text-gray-400 mt-1">
                      Último: {formatAmount(favorite.lastPaymentAmount)}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Category Selection */}
      {step === 'category' && (
        <Card>
          <div className="p-4 border-b">
            <h2 className="font-semibold">Selecione a categoria</h2>
          </div>
          <div className="p-4 grid grid-cols-3 gap-4">
            {getCategories().map((category) => {
              const config = CATEGORY_CONFIG[category];
              return (
                <button
                  key={category}
                  onClick={() => handleCategorySelect(category)}
                  className="p-4 border rounded-lg hover:bg-gray-50 transition-colors text-center"
                >
                  <div
                    className="w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${config.color}20` }}
                  >
                    <div style={{ color: config.color }}>{config.icon}</div>
                  </div>
                  <p className="text-sm font-medium">{config.label}</p>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Provider Selection */}
      {step === 'provider' && selectedCategory && (
        <Card>
          <div className="p-4 border-b">
            <h2 className="font-semibold">Selecione o provedor</h2>
          </div>
          <div className="divide-y">
            {getProvidersByCategory(selectedCategory).map((provider) => (
              <button
                key={provider.id}
                onClick={() => handleProviderSelect(provider)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <Receipt size={20} className="text-gray-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">{provider.name}</p>
                    <p className="text-sm text-gray-500">Ex: {provider.referenceExample}</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Reference Input */}
      {step === 'reference' && selectedProvider && (
        <Card>
          <div className="p-4 border-b">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Receipt size={20} className="text-blue-600" />
              </div>
              <div>
                <h2 className="font-semibold">{selectedProvider.name}</h2>
                <p className="text-sm text-gray-500">
                  Formato: {selectedProvider.referenceFormat}
                </p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Referência de pagamento
              </label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={selectedProvider.referenceExample}
                maxLength={selectedProvider.referenceLength}
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg tracking-wider"
              />
              <p className="text-xs text-gray-500 mt-1 text-center">
                {reference.length}/{selectedProvider.referenceLength} caracteres
              </p>
            </div>
            <Button
              onClick={() => validateBill()}
              disabled={validating || reference.length < 5}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
            >
              {validating ? <LoaderComponent /> : 'Validar Referência'}
            </Button>
          </div>
        </Card>
      )}

      {/* Confirm Payment */}
      {step === 'confirm' && billDetails && selectedProvider && (
        <Card>
          <div className="p-4 border-b bg-green-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle size={20} className="text-green-600" />
              </div>
              <div>
                <h2 className="font-semibold text-green-800">Referência Válida</h2>
                <p className="text-sm text-green-600">{selectedProvider.name}</p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            {/* Bill Details */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Referência:</span>
                <span className="font-mono">{billDetails.reference}</span>
              </div>
              {billDetails.customerName && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Cliente:</span>
                  <span>{billDetails.customerName}</span>
                </div>
              )}
              {billDetails.dueDate && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Vencimento:</span>
                  <span>
                    {fmtParede(billDetails.dueDate, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t">
                <span className="text-gray-500">Valor:</span>
                <span className="text-xl font-bold">{formatAmount(billDetails.amount)}</span>
              </div>
            </div>

            {/* Amount (if partial payment allowed) */}
            {selectedProvider.allowPartialPayment && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor a pagar
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-center text-xl font-semibold"
                />
                {selectedProvider.minAmount && (
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    Mínimo: {formatAmount(selectedProvider.minAmount)}
                  </p>
                )}
              </div>
            )}

            {/* PIN */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PIN de segurança
              </label>
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="****"
                maxLength={4}
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-center text-xl tracking-widest"
              />
            </div>

            {/* Save as Favorite */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSaveAsFavorite(!saveAsFavorite)}
                className={`p-2 rounded-lg ${
                  saveAsFavorite ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-400'
                }`}
              >
                {saveAsFavorite ? <Star size={20} /> : <StarOff size={20} />}
              </button>
              <span className="text-sm text-gray-600">Guardar como favorito</span>
            </div>
            {saveAsFavorite && (
              <input
                type="text"
                value={favoriteAlias}
                onChange={(e) => setFavoriteAlias(e.target.value)}
                placeholder="Nome do favorito (ex: Luz Casa)"
                className="w-full p-3 border rounded-lg"
              />
            )}

            <Button
              onClick={handlePayment}
              disabled={paying || !pin}
              className="w-full py-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2"
            >
              {paying ? (
                <LoaderComponent />
              ) : (
                <>
                  <Receipt size={20} />
                  Pagar {formatAmount(parseFloat(amount) || billDetails.amount)}
                </>
              )}
            </Button>
          </div>
        </Card>
      )}

      {/* Result Modal */}
      <Modal isOpen={showResult} onClose={() => setShowResult(false)} title="Pagamento">
        <div className="text-center py-6">
          {result?.success ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Pagamento Concluído!</h3>
              <p className="text-gray-600 mb-4">{formatAmount(result.amount)}</p>
              <p className="text-sm text-gray-500">ID: {result.transactionId}</p>
              {result.fee > 0 && (
                <p className="text-xs text-gray-400 mt-2">Taxa: {formatAmount(result.fee)}</p>
              )}
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Pagamento Falhou</h3>
              <p className="text-gray-600">Por favor, tente novamente.</p>
            </>
          )}
          <Button
            onClick={() => {
              setShowResult(false);
              setResult(null);
            }}
            className="mt-6 px-8 py-2 bg-blue-600 text-white rounded-lg"
          >
            Fechar
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default BankingBills;
